import { Static, Type } from '@sinclair/typebox';
import { AddressSchema } from './professional';
import { ServiceAreaTypeSchema } from './professional/service-area.schema';

/**
 * Places API — schémas partagés du proxy backend de geocoding / autocomplete.
 *
 * Pourquoi un proxy plutôt que l'appel direct à Google Maps JS depuis le
 * navigateur :
 * - la clé Google reste côté serveur (jamais bundlée),
 * - le provider sous-jacent peut être swap (Google, BAN/API gouv, Mapbox…)
 *   sans toucher le frontend,
 * - rate-limit + circuit breaker + logs centralisés via le module résilience.
 *
 * Côté Google : autocomplete et getDetails partagent une « session » (un même
 * `session_token` propagé à chaque appel) → tarification consolidée. Le token
 * est généré par le frontend (uuid v4) au montage du composant et ré-utilisé
 * pendant toute la session d'édition.
 */

/**
 * Granularité des suggestions — pilote `types` côté Google Places.
 *
 * - `address` → autocomplete adresses (`types=address`).
 * - `address_poi` → adresses + établissements/POI (« Palais des congrès »…) :
 *   aucun filtre `types` (l'API legacy n'accepte qu'une collection par requête,
 *   l'union `address|establishment` est invalide — omettre le param couvre les
 *   deux). Utilisé par les champs adresse du tunnel/formulaire de course VTC.
 * - `geocode` → adresses précises + villes + codes postaux (`types=geocode`) :
 *   pour l'adresse d'un client final, dont le pro ne connaît souvent que la ville.
 * - `cities`  → autocomplete villes (`types=(cities)`).
 * - `regions` → autocomplete zones administratives (`types=(regions)`), couvre
 *   villes + départements + régions en un seul call. Le `area_type` précis
 *   (CITY / DEPARTMENT / REGION) est dérivé par le backend depuis les `types`
 *   renvoyés par Google et exposé sur chaque prediction.
 *
 * Note : on n'expose pas un mode `departments` séparé — l'autocomplete Google
 * legacy n'accepte que les collections `(cities)` / `(regions)`, pas les types
 * `administrative_area_level_*` comme filtre.
 */
export const PlacesAutocompleteModeSchema = Type.Union([
  Type.Literal('address'),
  Type.Literal('address_poi'),
  Type.Literal('geocode'),
  Type.Literal('cities'),
  Type.Literal('regions'),
]);
export type PlacesAutocompleteMode = Static<typeof PlacesAutocompleteModeSchema>;
/** Liste des modes dérivée du schéma : seule source pour la validation backend et Swagger. */
export const PLACES_AUTOCOMPLETE_MODES: readonly PlacesAutocompleteMode[] = PlacesAutocompleteModeSchema.anyOf.map(
  (literal): PlacesAutocompleteMode => literal.const,
);

/** Une prédiction renvoyée par l'autocomplete. */
export const PlacesAutocompletePredictionSchema = Type.Object({
  /** Libellé affichable, ex : « Lyon, France ». */
  description: Type.String(),
  /** Identifiant opaque à passer ensuite à `/places/details`. */
  place_id: Type.String(),
  /**
   * Type de zone administrative dérivé du champ `types` renvoyé par Google.
   * Présent uniquement quand `mode=regions` et que la prediction correspond à
   * une ville, un département ou une région (countries / postal_codes filtrés
   * en amont). Absent pour les modes `address` et `cities`.
   */
  area_type: Type.Optional(ServiceAreaTypeSchema),
});
export type PlacesAutocompletePredictionObject = Static<typeof PlacesAutocompletePredictionSchema>;

export const PlacesAutocompleteResponseSchema = Type.Object({
  predictions: Type.Array(PlacesAutocompletePredictionSchema),
});
export type PlacesAutocompleteResponseObject = Static<typeof PlacesAutocompleteResponseSchema>;

export const PlacesDetailsResponseSchema = Type.Object({
  address: AddressSchema,
});
export type PlacesDetailsResponseObject = Static<typeof PlacesDetailsResponseSchema>;
